import type { Nullable } from '@iheartjane/types';
import { useCallback, useEffect, useState } from 'react';
import type { Fetcher } from 'react-router';
import { useFetcher, useFetchers, useMatches } from 'react-router';

import type { CartActionResult } from '@jane/bloom/actions/cart.server';
import { useStoreSwitcher } from '@jane/bloom/hooks/storeSwitcher/useStoreSwitcher';
import { useRootLoaderData } from '@jane/bloom/hooks/useRootLoaderData';
import { useBrandSpecialShortfallNotice } from '@jane/bloom/providers/brandSpecialShortfallProvider';
import type { CardLocation } from '@jane/bloom/services/tracking/Events';
import { trackAddedProductToCart } from '@jane/bloom/services/tracking/track';
import type { ActionResult } from '@jane/bloom/types/remixAction';
import { isRouteInGroup, pdpPages } from '@jane/bloom/utils/routes/routeIds';
import type { ProductViewModel } from '@jane/bloom/viewModels/productDetail';
import type { ProductSearchResultViewModel } from '@jane/bloom/viewModels/productSearch';

interface AddToCartConfigBase {
  productId: number;
  rowId?: number | string;
}

export interface AddToCartFormArgs {
  action: string;
  fetcherKey: string;
  method: 'post';
  navigate: boolean;
  preventScrollReset: boolean;
}

interface AddToCartConfig extends AddToCartConfigBase {
  cardLocation?: CardLocation;
  index?: number;
  product: ProductSearchResultViewModel | ProductViewModel;
  quantity: number;
  selectedWeight?: Nullable<string> | undefined;
}

const FETCHER_KEY_PREFIX = 'add-to-cart';
const fetcherKey = ({ productId, rowId }: AddToCartConfigBase) =>
  `${FETCHER_KEY_PREFIX}-${productId}-row-${rowId ?? ''}`;

export const buildAddToCartFormArgs = ({
  productId,
  rowId,
}: AddToCartConfigBase): AddToCartFormArgs => ({
  action: `/api/add-to-cart`,
  fetcherKey: fetcherKey({ productId, rowId }),
  method: 'post' as const,
  navigate: false,
  preventScrollReset: true,
});

/** True when an add-to-cart fetcher key belongs to the given product (any row). */
export const isAddToCartFetcherFor = (key: string, productId: number) =>
  key.startsWith(fetcherKey({ productId }));

export const useAddToCartFetchers = (
  productId?: number
): (Fetcher<ActionResult> & { key: string })[] => {
  const fetchers = useFetchers();
  return fetchers.filter(({ key }) =>
    productId === undefined
      ? key.startsWith(FETCHER_KEY_PREFIX)
      : isAddToCartFetcherFor(key, productId)
  );
};

export const useAddToCartConfig = ({
  cardLocation,
  index,
  product,
  productId,
  quantity = 1,
  rowId,
  selectedWeight,
}: AddToCartConfig) => {
  const fetcher = useFetcher<CartActionResult>({
    key: fetcherKey({ productId, rowId }),
  });
  useBrandSpecialShortfallNotice(fetcher.data);
  const { cart, featureFlags } = useRootLoaderData();
  const { searchReservationMode, storeType } = useStoreSwitcher();
  const isPdpPage = useMatches().some(({ id }) => isRouteInGroup(pdpPages, id));
  const [hasSubmitted, setHasSubmitted] = useState(false);

  const trackAddToCart = useCallback(() => {
    trackAddedProductToCart(
      {
        cardLocation,
        cart,
        index,
        location: isPdpPage ? 'productDetailPage' : 'menu',
        product,
        quantity,
        reservationMode: searchReservationMode,
        selectedWeight,
        storeType,
      },
      featureFlags
    );
  }, [
    cardLocation,
    cart,
    featureFlags,
    index,
    isPdpPage,
    product,
    quantity,
    searchReservationMode,
    selectedWeight,
    storeType,
  ]);

  /**
   * Track add to cart event when fetcher state changes to idle after submitting.
   * This is to ensure that the event is tracked after the cart is updated.
   * Only successful adds count — a rejected add (e.g. an unavailable product)
   * never made it into the cart.
   */
  useEffect(() => {
    if (fetcher.state !== 'idle' || !hasSubmitted) return;

    setHasSubmitted(false);
    if (fetcher.data?.success) trackAddToCart();
  }, [fetcher.data, fetcher.state, hasSubmitted, trackAddToCart]);

  useEffect(() => {
    if (fetcher.state === 'submitting') {
      setHasSubmitted(true);
    }
  }, [fetcher.state]);

  return {
    fetcher,
    /**
     * Fetcher props to add to a Remix Form or Submit function.
     */
    formArgs: buildAddToCartFormArgs({ productId, rowId }),
  };
};
